import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export default function Services() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/ydelser');
        if (response.ok) {
          const data = await response.json();
          setServices(data);
        }
      } catch (err) {
        console.error('Error fetching services:', err);
      }
    };

    fetchServices();
  }, []);

  return (
    <>
      <Helmet>
        <title>Vores ydelser | Tømrer- og malerarbejde på Djursland | Tømrerfirmaet RNS</title>
        <meta name="description" content="Se vores brede udvalg af håndværksydelser på Djursland, i Grenaa, Ebeltoft og Balle. Vi tilbyder sommerhusrenovering, nyt tag, vinduer og døre, tilbygning, ombygning samt malerarbejde." />
        <meta property="og:title" content="Vores ydelser | Tømrer- og malerarbejde på Djursland | Tømrerfirmaet RNS" />
        <meta property="og:description" content="Se vores brede udvalg af håndværksydelser på Djursland, i Grenaa, Ebeltoft og Balle. Vi tilbyder sommerhusrenovering, nyt tag, vinduer og døre, tilbygning, ombygning samt malerarbejde." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/d1f69564-4a06-4909-b6f1-fa1f6ad6911e/img-0027-scaled.jpg" />
        <link rel="canonical" href="https://rns.dk/ydelser" />
        <html lang="da-DK" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 bg-slate-900 text-white">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/d1f69564-4a06-4909-b6f1-fa1f6ad6911e/img-0027-scaled.jpg"
            alt="Professionelle håndværksydelser og tømrerarbejde på Djursland - Tømrerfirmaet RNS"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-dark/90 to-brand-dark" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center space-y-4">
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl">
            Vi tilbyder professionelt håndværk
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Uanset om du skal have renoveret sommerhuset, lagt nyt tag, udskiftet vinduer og døre eller malet facaden, står vi klar med professionel ekspertise.
          </p>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <div key={service.id} className="bg-slate-50 rounded-none overflow-hidden border-b border-slate-300 flex flex-col justify-between hover:shadow-md transition-all">
                <div>
                  {service.hero_billede && service.hero_billede[0] && (
                    <img
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${service.hero_billede[0].replace('https://assets.auxodev.dk/', '')}`}
                      alt={`${service.titel?.da || service.titel} - Professionelt udført på Djursland af Tømrerfirmaet RNS`}
                      className="w-full h-56 object-cover"
                    />
                  )}
                  <div className="p-6 space-y-3">
                    <h2 className="font-display font-bold text-xl text-slate-900">
                      {service.titel.da}
                    </h2>
                    <p className="text-sm text-slate-600 leading-relaxed line-clamp-4">
                      {service.meta_description?.da || "Professionelt udført tømrer- og malerarbejde."}
                    </p>
                  </div>
                </div>
                <div className="p-6 pt-0">
                  <Link
                    to={`/ydelser/${service.slug.da || service.slug}`}
                    className="w-full inline-flex items-center justify-center gap-2 bg-white hover:bg-brand-primary hover:text-white text-brand-primary border border-slate-200 font-semibold py-2.5 px-4 rounded-none transition-all text-sm"
                  >
                    Læs mere om {service.titel.da.toLowerCase()}
                    <i className="fa-solid fa-arrow-right text-xs" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 bg-slate-50 border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-6">
          <h2 className="font-display font-extrabold text-3xl text-slate-900">
            Har du et specifikt projekt i tankerne?
          </h2>
          <p className="text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Vi skræddersyr altid vores løsninger efter dine ønsker og behov. Kontakt os i dag for en uforpligtende snak og et godt tilbud.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <Link
              to="/kontakt"
              className="bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold py-2.5 px-5 text-sm sm:py-3.5 sm:px-8 sm:text-base rounded-none transition-all shadow-sm"
            >
              Kontakt os her
            </Link>
            <a
              href="tel:22439147"
              className="bg-white hover:bg-slate-50 text-brand-primary border border-slate-200 font-semibold py-2.5 px-5 text-sm sm:py-3.5 sm:px-8 sm:text-base rounded-none transition-all shadow-sm"
            >
              Ring: +45 22 43 91 47
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
